/**
 * Organizasyon Şeması — tenant-wide interactive org chart.
 *
 * View:
 *   - Everyone in the tenant can view.
 *   - super_admin / ultra_admin can add / edit / delete / reparent.
 *
 * Layout: recursive tree rendered as nested flex rows with CSS
 * pseudo-element connectors (::before / ::after). Cards are colored
 * per node's `color` value (see ORG_PALETTE). Clicking a card opens
 * a right-side editor sheet.
 *
 * Sharing: super_admin mints a 7-day public token. The public view
 * lives at `/org-chart-share/:token`.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Share2,
  Trash2,
  Copy,
  Check,
  Link as LinkIcon,
  Loader2,
  Sparkles,
  X,
  PencilLine,
  Network,
  Download,
  FileDown,
  Image as ImageIcon,
  ChevronDown,
  MoreVertical,
  ZoomIn,
  ZoomOut,
  Hand,
  ArrowLeftRight,
  CornerRightDown,
  Search,
  Maximize,
  Expand,
  Shrink,
  MoveLeft,
  MoveRight,
  Link2,
  StickyNote,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuthStore } from "../store/authStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { OrgTree, ORG_PALETTE } from "../components/orgchart/OrgTree";
import { UserPicker } from "../components/orgchart/UserPicker";
import { LinkLayer } from "../components/orgchart/LinkLayer";
import { StickyNotesLayer } from "../components/orgchart/StickyNotes";
import useTenantWebSocket from "../hooks/useTenantWebSocket";

// -----------------------------------------------------------------------------
// Utils — flat list → tree
// -----------------------------------------------------------------------------
function buildTree(nodes) {
  const byId = {};
  nodes.forEach((n) => (byId[n.id] = { ...n, children: [] }));
  const roots = [];
  nodes.forEach((n) => {
    if (n.parent_id && byId[n.parent_id]) {
      byId[n.parent_id].children.push(byId[n.id]);
    } else {
      roots.push(byId[n.id]);
    }
  });
  const sortRec = (list) => {
    list.sort(
      (a, b) =>
        (a.order || 0) - (b.order || 0) ||
        (a.created_at || "").localeCompare(b.created_at || ""),
    );
    list.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

// -----------------------------------------------------------------------------
// Lineage helpers — used by the UserPicker to badge / disable users
// Simple e-mail shape check — used for the red inline warning only.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function KindSizePicker({ kind, setKind, size, setSize, testPrefix, disabled }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Kart tipi</Label>
        <div className="mt-1 flex gap-1">
          {[["person", "Kişi"], ["department", "Departman"]].map(([v, l]) => (
            <button
              key={v}
              type="button"
              disabled={disabled}
              onClick={() => setKind(v)}
              data-testid={`${testPrefix}-kind-${v}`}
              className={`flex-1 rounded-lg border px-2 py-1.5 text-[11px] font-semibold transition-colors ${
                kind === v
                  ? "border-violet-500 bg-violet-50 text-violet-700"
                  : "border-zinc-200 text-zinc-500 hover:bg-zinc-50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Boyut</Label>
        <div className="mt-1 flex gap-1">
          {[["sm", "Küçük"], ["md", "Normal"], ["lg", "Büyük"]].map(([v, l]) => (
            <button
              key={v}
              type="button"
              disabled={disabled}
              onClick={() => setSize(v)}
              data-testid={`${testPrefix}-size-${v}`}
              className={`flex-1 rounded-lg border px-1 py-1.5 text-[10.5px] font-semibold transition-colors ${
                size === v
                  ? "border-violet-500 bg-violet-50 text-violet-700"
                  : "border-zinc-200 text-zinc-500 hover:bg-zinc-50"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
// who are already on the top→bottom path of the node being edited.
// -----------------------------------------------------------------------------
function ancestorUserIds(nodes, startParentId) {
  const map = {};
  nodes.forEach((n) => (map[n.id] = n));
  const ids = new Set();
  const visited = new Set();
  let cur = startParentId || null;
  while (cur && !visited.has(cur)) {
    visited.add(cur);
    const n = map[cur];
    if (!n) break;
    if (n.user_id) ids.add(n.user_id);
    cur = n.parent_id || null;
  }
  return ids;
}

function descendantUserIds(nodes, rootId) {
  const ids = new Set();
  const stack = [rootId];
  while (stack.length) {
    const cur = stack.pop();
    nodes.forEach((n) => {
      if (n.parent_id === cur) {
        if (n.user_id) ids.add(n.user_id);
        stack.push(n.id);
      }
    });
  }
  return ids;
}

// user_ids that appear anywhere in the chart (informational "kullanımda" badge)
function usedUserIds(nodes) {
  const s = new Set();
  nodes.forEach((n) => n.user_id && s.add(n.user_id));
  return s;
}

// -----------------------------------------------------------------------------
// Main page
// -----------------------------------------------------------------------------
export default function OrgChart() {
  const { user: me } = useAuthStore();
  const canEdit = me?.role === "super_admin" || me?.role === "ultra_admin";

  const [charts, setCharts] = useState([]);
  const [activeChartId, setActiveChartId] = useState(null);
  const [nodes, setNodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // node id
  // "add child under X" — id of parent while add flow is open. null = new root.
  const [addUnder, setAddUnder] = useState(undefined);
  // Sibling placement hint — { referenceId, place: "before" | "after" }
  // populated when the left/right arrow buttons trigger the add flow.
  const [addSiblingHint, setAddSiblingHint] = useState(null);
  // "add parent above X" — root node id when the ↑ flow is open.
  const [addAboveId, setAddAboveId] = useState(null);
  const [shareDlgOpen, setShareDlgOpen] = useState(false);

  // ---- Canvas zoom & pan ----
  const [zoom, setZoom] = useState(1);
  const [panMode, setPanMode] = useState(false);
  const scrollRef = useRef(null);
  const zoomRef = useRef(1);
  const panRef = useRef(null);
  useEffect(() => {
    zoomRef.current = zoom;
  }, [zoom]);
  const ZOOM_MIN = 0.3;
  const ZOOM_MAX = 2.5;
  const clampZoom = (z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z));

  // Drag & drop between cards → {srcId, tgtId} pending action dialog.
  const [dndDrop, setDndDrop] = useState(null);

  // ---- Search, collapse & fit ----
  const [query, setQuery] = useState("");
  const [activeMatchIdx, setActiveMatchIdx] = useState(0);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [fullscreen, setFullscreen] = useState(false);

  // ---- Miro-style extras ----
  const [links, setLinks] = useState([]);
  const [notes, setNotes] = useState([]);
  const [linkMode, setLinkMode] = useState(false);
  const [linkSource, setLinkSource] = useState(null);
  const [linkToDelete, setLinkToDelete] = useState(null);
  const [multiSel, setMultiSel] = useState(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const wrapRef = useRef(null);

  const loadCharts = useCallback(async () => {
    try {
      const { data } = await api.get("/org-chart/charts");
      const list = data || [];
      setCharts(list);
      // Preserve selection where possible; fall back to first tab.
      setActiveChartId((cur) => {
        if (cur && list.some((c) => c.id === cur)) return cur;
        return list[0]?.id || null;
      });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  const load = useCallback(
    async (chartId, opts2 = {}) => {
      if (!chartId) return;
      if (!opts2.silent) setLoading(true);
      try {
        const [n, l, no] = await Promise.all([
          api.get("/org-chart/nodes", { params: { chart_id: chartId } }),
          api.get("/org-chart/links", { params: { chart_id: chartId } }),
          api.get("/org-chart/notes", { params: { chart_id: chartId } }),
        ]);
        setNodes(n.data || []);
        setLinks(l.data || []);
        setNotes(no.data || []);
      } catch (err) {
        if (!opts2.silent)
          toast.error(formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        if (!opts2.silent) setLoading(false);
      }
    },
    [],
  );

  const loadUsers = useCallback(async () => {
    // Regular tenant users list — used to power the "kullanıcıdan seç"
    // picker on nodes so we don't hand-type teammate names.
    try {
      const { data } = await api.get("/users");
      setUsers(data || []);
    } catch {
      /* non-fatal — picker just stays empty */
    }
  }, []);

  useEffect(() => {
    loadCharts();
    loadUsers();
  }, [loadCharts, loadUsers]);

  useEffect(() => {
    if (activeChartId) load(activeChartId);
  }, [activeChartId, load]);

  // Live collaboration: reload quietly when anyone in the tenant changes the chart.
  const activeChartIdRef = useRef(null);
  useEffect(() => {
    activeChartIdRef.current = activeChartId;
  }, [activeChartId]);
  const wsTimerRef = useRef(null);
  useTenantWebSocket(
    useCallback(
      (msg) => {
        if (msg?.event !== "org_chart_changed") return;
        clearTimeout(wsTimerRef.current);
        wsTimerRef.current = setTimeout(() => {
          const cid = activeChartIdRef.current;
          if (cid && (!msg.chart_id || msg.chart_id === cid)) {
            load(cid, { silent: true });
          }
        }, 400);
      },
      [load],
    ),
  );

  // Chart-tab management — rename / add / delete
  const [newChartOpen, setNewChartOpen] = useState(false);
  const addChart = async (name) => {
    try {
      const { data } = await api.post("/org-chart/charts", { name });
      await loadCharts();
      setActiveChartId(data.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const renameChart = async (id, name) => {
    try {
      await api.patch(`/org-chart/charts/${id}`, { name });
      await loadCharts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteChart = async (id) => {
    try {
      await api.delete(`/org-chart/charts/${id}`);
      // Reset active tab BEFORE reloading; new default may be provisioned.
      setActiveChartId(null);
      await loadCharts();
      toast.success("Şema silindi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const tree = useMemo(() => buildTree(nodes), [nodes]);
  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u));
    return m;
  }, [users]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selected) || null,
    [nodes, selected],
  );

  // "+ Yan" → add a sibling: create a child under the current node's parent.
  // `place` is "before" (left) or "after" (right). Backend renumbers
  // the siblings' `order` so the new node lands exactly on that side.
  const onAddSibling = useCallback(
    (nodeId, place) => {
      const n = nodes.find((x) => x.id === nodeId);
      if (!n) return;
      setAddUnder(n.parent_id || null);
      setAddSiblingHint({ referenceId: nodeId, place: place || "after" });
    },
    [nodes],
  );

  const onAddChild = useCallback((nodeId) => {
    setAddSiblingHint(null);
    setAddUnder(nodeId);
  }, []);

  // "↑ Üst" → insert a new node ABOVE a root: new node becomes the root,
  // the old root drops one level below it.
  const onAddParent = useCallback((nodeId) => {
    setAddSiblingHint(null);
    setAddAboveId(nodeId);
    setAddUnder(null);
  }, []);

  // Global keyboard shortcuts. Once a card is selected (click), the
  // operator can add relatives without touching the mouse. Bindings:
  //   Alt+ArrowLeft  — add sibling to the LEFT
  //   Alt+ArrowRight — add sibling to the RIGHT
  //   Enter          — add child BELOW
  //   Escape         — close the editor sheet
  useEffect(() => {
    if (!canEdit) return undefined;
    const onKey = (e) => {
      // Skip when user is typing plain text — but Alt+Arrow modifiers
      // are safe to intercept even from an input (they never conflict
      // with normal text entry).
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      const isTextTarget =
        ["input", "textarea", "select"].includes(tag) || t?.isContentEditable;
      const isPlainKey = !e.altKey;
      if (isTextTarget && isPlainKey) return;
      // Also skip when a button has focus and user hits Enter — that's
      // the button's own click, not our shortcut.
      if (tag === "button" && e.key === "Enter") return;
      // Never steal keys while another dialog is open.
      if (addUnder !== undefined || shareDlgOpen) return;
      if (!selected) return;
      const n = nodes.find((x) => x.id === selected);
      if (!n) return;

      if (e.altKey && e.key === "ArrowLeft") {
        e.preventDefault();
        setSelected(null);
        onAddSibling(n.id, "before");
      } else if (e.altKey && e.key === "ArrowRight") {
        e.preventDefault();
        setSelected(null);
        onAddSibling(n.id, "after");
      } else if (e.altKey && e.key === "ArrowUp" && !n.parent_id) {
        e.preventDefault();
        setSelected(null);
        onAddParent(n.id);
      } else if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        setSelected(null);
        onAddChild(n.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [canEdit, selected, nodes, addUnder, shareDlgOpen, onAddSibling, onAddChild, onAddParent]);

  const isEmpty = !loading && nodes.length === 0;

  // Mouse-wheel zoom over the canvas (anchored at the cursor). Native
  // listener with passive:false so preventDefault actually works.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const old = zoomRef.current;
      const nz = clampZoom(old * Math.exp(-e.deltaY * 0.0014));
      if (nz === old) return;
      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const mx = (el.scrollLeft + cx) / old;
      const my = (el.scrollTop + cy) / old;
      setZoom(nz);
      requestAnimationFrame(() => {
        el.scrollLeft = mx * nz - cx;
        el.scrollTop = my * nz - cy;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isEmpty]);

  // Pan-mode drag: left-button drag scrolls the canvas.
  const onPanMouseDown = (e) => {
    if (!panMode || e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    e.preventDefault();
    panRef.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop };
    el.style.cursor = "grabbing";
    const move = (ev) => {
      const p = panRef.current;
      if (!p) return;
      el.scrollLeft = p.sl - (ev.clientX - p.x);
      el.scrollTop = p.st - (ev.clientY - p.y);
    };
    const up = () => {
      panRef.current = null;
      el.style.cursor = "";
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const nodeLabel = (n) =>
    n?.title || n?.person_name || (n?.user_id && userMap[n.user_id]?.name) || "Adsız";
  const personLabel = (n) =>
    (n?.user_id && userMap[n.user_id]?.name) || n?.person_name || "— boş —";

  const doDndMove = async () => {
    if (!dndDrop) return;
    try {
      await api.patch(`/org-chart/nodes/${dndDrop.srcId}`, { parent_id: dndDrop.tgtId });
      toast.success("Düğüm taşındı");
      setDndDrop(null);
      load(activeChartId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Taşınamadı");
    }
  };

  const doDndSwap = async () => {
    if (!dndDrop) return;
    try {
      await api.post("/org-chart/nodes/swap-people", {
        node_a: dndDrop.srcId,
        node_b: dndDrop.tgtId,
      });
      toast.success("Kişiler yer değiştirdi");
      setDndDrop(null);
      load(activeChartId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Takas yapılamadı");
    }
  };

  const doDndBeside = async (side) => {
    if (!dndDrop) return;
    try {
      await api.post(`/org-chart/nodes/${dndDrop.srcId}/move-beside`, {
        target_id: dndDrop.tgtId,
        side,
      });
      toast.success(side === "left" ? "Soluna taşındı" : "Sağına taşındı");
      setDndDrop(null);
      load(activeChartId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Taşınamadı");
    }
  };

  // ---- Card click routing: link mode > shift multi-select > editor ----
  const onSelectNode = (id, e) => {
    if (linkMode && canEdit) {
      if (!linkSource) {
        setLinkSource(id);
        return;
      }
      if (linkSource === id) {
        setLinkSource(null);
        return;
      }
      createLink(linkSource, id);
      return;
    }
    if (e?.shiftKey && canEdit) {
      setMultiSel((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
      return;
    }
    setSelected(id);
  };

  const createLink = async (fromId, toId) => {
    try {
      const { data } = await api.post("/org-chart/links", {
        chart_id: activeChartId,
        from_id: fromId,
        to_id: toId,
      });
      setLinks((l) => [...l, data]);
      toast.success("Bağlantı eklendi — silmek için çizgiye tıklayın");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Bağlanamadı");
    }
    setLinkSource(null);
  };

  const doDeleteLink = async () => {
    if (!linkToDelete) return;
    try {
      await api.delete(`/org-chart/links/${linkToDelete}`);
      setLinks((l) => l.filter((x) => x.id !== linkToDelete));
      toast.success("Bağlantı silindi");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    }
    setLinkToDelete(null);
  };

  // ---- Sticky notes ----
  const addNote = async () => {
    const el = scrollRef.current;
    const z = zoomRef.current || 1;
    const x = el ? (el.scrollLeft + 90 + Math.random() * 60) / z : 40;
    const y = el ? (el.scrollTop + 90 + Math.random() * 60) / z : 40;
    try {
      const { data } = await api.post("/org-chart/notes", {
        chart_id: activeChartId,
        text: "",
        color: "amber",
        x,
        y,
      });
      setNotes((n) => [...n, data]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Not eklenemedi");
    }
  };

  const updateNote = (id, patch, persist) => {
    setNotes((ns) => ns.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    if (persist) api.patch(`/org-chart/notes/${id}`, patch).catch(() => {});
  };

  const deleteNote = (id) => {
    setNotes((ns) => ns.filter((n) => n.id !== id));
    api.delete(`/org-chart/notes/${id}`).catch(() => {});
  };

  // ---- Bulk operations on the multi-selection ----
  const bulkColor = async (color) => {
    try {
      await api.post("/org-chart/nodes/bulk-color", { ids: [...multiSel], color });
      toast.success("Renk uygulandı");
      load(activeChartId, { silent: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Uygulanamadı");
    }
  };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/org-chart/nodes/bulk-delete", { ids: [...multiSel] });
      toast.success(`${data.deleted} kart silindi`);
      setMultiSel(new Set());
      setBulkDeleteOpen(false);
      load(activeChartId);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Silinemedi");
    }
  };

  // Legend: department header nodes → color + label list.
  const legendItems = useMemo(() => {
    const seen = new Set();
    return nodes
      .filter((n) => n.kind === "department" && n.title)
      .filter((n) => {
        const k = `${n.color}|${n.title}`;
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
  }, [nodes]);

  // ---- Search: match by title / person / linked user name ----
  const matches = useMemo(() => {
    const q = query.trim().toLocaleLowerCase("tr");
    if (!q) return [];
    return nodes
      .filter((n) => {
        const hay = [n.title, n.person_name, n.user_id ? userMap[n.user_id]?.name : null]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("tr");
        return hay.includes(q);
      })
      .map((n) => n.id);
  }, [query, nodes, userMap]);

  const focusMatch = useCallback((id) => {
    requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="org-chart-node-${id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    });
  }, []);

  // Auto-expand collapsed ancestors of matches, then focus the first match.
  useEffect(() => {
    if (!matches.length) {
      setActiveMatchIdx(0);
      return;
    }
    setCollapsed((prev) => {
      if (!prev.size) return prev;
      const byId = {};
      nodes.forEach((n) => {
        byId[n.id] = n;
      });
      const next = new Set(prev);
      matches.forEach((id) => {
        let cur = byId[id]?.parent_id;
        const seen = new Set();
        while (cur && !seen.has(cur)) {
          seen.add(cur);
          next.delete(cur);
          cur = byId[cur]?.parent_id;
        }
      });
      return next.size === prev.size ? prev : next;
    });
    setActiveMatchIdx(0);
    focusMatch(matches[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches]);

  // Chart switch → clear search & collapse state.
  useEffect(() => {
    setQuery("");
    setCollapsed(new Set());
    setZoom(1);
    setPanMode(false);
    setLinkMode(false);
    setLinkSource(null);
    setMultiSel(new Set());
  }, [activeChartId]);

  const toggleCollapse = useCallback((id) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Fit the whole tree into the visible canvas.
  const fitToScreen = (fsOverride) => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = el.querySelectorAll(".node-card");
    if (!cards.length) return;
    const isFs = typeof fsOverride === "boolean" ? fsOverride : fullscreen;
    const cur = zoomRef.current;
    // Measure the true content extents from the cards themselves — the tree
    // container is a block element stretched to full width (min-w-full), so
    // its own rect is useless for fitting.
    let minL = Infinity;
    let minT = Infinity;
    let maxR = -Infinity;
    let maxB = -Infinity;
    cards.forEach((c) => {
      const r = c.getBoundingClientRect();
      minL = Math.min(minL, r.left);
      minT = Math.min(minT, r.top);
      maxR = Math.max(maxR, r.right);
      maxB = Math.max(maxB, r.bottom);
    });
    const naturalW = (maxR - minL) / cur;
    const naturalH = (maxB - minT + 24) / cur; // avatars overflow the cards a bit
    if (naturalW <= 0 || naturalH <= 0) return;
    const availW = el.clientWidth - 56;
    const availH = isFs
      ? el.clientHeight - 56
      : Math.max(240, window.innerHeight * 0.78 - 56);
    // Sığdırma asla %100'ün üzerine BÜYÜTMEZ — şema ekrandan küçükse
    // doğal boyutunda (%100) kalır; sadece taşarsa küçültülür.
    setZoom(clampZoom(Math.min(1, availW / naturalW, availH / naturalH)));
    requestAnimationFrame(() => {
      el.scrollLeft = 0;
      el.scrollTop = 0;
    });
  };

  // Fullscreen (big view) toggle — Esc closes, body scroll locked while open.
  const prevZoomRef = useRef(1);
  const toggleFullscreen = () => {
    setFullscreen((v) => {
      const next = !v;
      if (next) {
        prevZoomRef.current = zoomRef.current;
        setTimeout(() => fitToScreen(true), 120);
      } else {
        setZoom(prevZoomRef.current || 1);
      }
      return next;
    });
  };
  useEffect(() => {
    if (!fullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !["INPUT", "TEXTAREA"].includes(e.target?.tagName)) {
        setFullscreen(false);
        setZoom(prevZoomRef.current || 1);
      }
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [fullscreen]);

  // Ref-less DOM lookup — the canvas is a stable data-testid, no need
  // to plumb refs through the tree component.
  const [exporting, setExporting] = useState(false);
  const handleExport = async (fmt) => {
    const el =
      document.querySelector('[data-testid="org-chart-zoom-wrapper"]') ||
      document.querySelector('[data-testid="org-chart-canvas"]');
    if (!el) {
      toast.error("İçerik hazır değil");
      return;
    }
    setExporting(true);
    // Export at 100% zoom so the PDF/PNG isn't scaled or clipped.
    const prevZoom = zoomRef.current;
    if (prevZoom !== 1) {
      setZoom(1);
      await new Promise((r) => setTimeout(r, 120));
    }
    try {
      const { exportOrgChartPdf, exportOrgChartPng } = await import(
        "../components/orgchart/exportOrgChart"
      );
      const label = me?.tenant_name || "organizasyon-semasi";
      if (fmt === "pdf") await exportOrgChartPdf(el, label);
      else await exportOrgChartPng(el, label);
      toast.success("İndirildi");
    } catch (err) {
      toast.error(err?.message || "Dışa aktarma başarısız");
    } finally {
      setExporting(false);
      if (prevZoom !== 1) setZoom(prevZoom);
    }
  };

  return (
    <div className="space-y-6">
      <Header
        canEdit={canEdit}
        onAddRoot={() => {
          setAddSiblingHint(null);
          setAddUnder(null);
        }}
        onShare={() => setShareDlgOpen(true)}
        onExport={handleExport}
        exporting={exporting}
        hasData={nodes.length > 0}
      />

      {/* Chart tabs — multi-chart support. Auto-provisioned on first load. */}
      <ChartTabs
        charts={charts}
        activeId={activeChartId}
        onSelect={setActiveChartId}
        canEdit={canEdit}
        onAdd={() => setNewChartOpen(true)}
        onRename={renameChart}
        onDelete={deleteChart}
      />

      {loading ? (
        <div className="flex h-64 items-center justify-center rounded-2xl border border-zinc-200 bg-white">
          <Loader2 className="h-6 w-6 animate-spin text-zinc-400" />
        </div>
      ) : isEmpty ? (
        <EmptyState canEdit={canEdit} onAddRoot={() => { setAddSiblingHint(null); setAddUnder(null); }} />
      ) : (
        <div
          className={
            fullscreen
              ? "fixed inset-0 z-[100] flex flex-col bg-zinc-100/95 p-3 backdrop-blur-sm sm:p-5"
              : "relative"
          }
          data-testid="org-chart-stage"
        >
          <div
            ref={scrollRef}
            data-testid="org-chart-canvas"
            onMouseDown={onPanMouseDown}
            className={[
              "relative overflow-auto rounded-2xl border border-zinc-200 bg-gradient-to-br from-zinc-50 via-white to-violet-50/40",
              fullscreen ? "flex-1 shadow-2xl" : "max-h-[78vh]",
              panMode ? "cursor-grab select-none" : "",
            ].join(" ")}
          >
            <div
              ref={wrapRef}
              data-testid="org-chart-zoom-wrapper"
              style={{ zoom }}
              className={`relative min-h-[380px] w-fit min-w-full p-6 ${panMode ? "pan-mode" : ""}`}
            >
              <OrgTree
                roots={tree}
                onSelect={onSelectNode}
                onAddChild={canEdit ? onAddChild : null}
                onAddSibling={canEdit ? onAddSibling : null}
                onAddParent={canEdit ? onAddParent : null}
                selectedId={selected}
                userMap={userMap}
                dnd={canEdit && !panMode && !linkMode}
                onNodeDrop={(srcId, tgtId) => setDndDrop({ srcId, tgtId })}
                highlightIds={new Set(matches)}
                collapsedIds={collapsed}
                onToggleCollapse={toggleCollapse}
                multiIds={multiSel}
                linkSourceId={linkSource}
              />
              <LinkLayer
                links={links}
                wrapperRef={wrapRef}
                zoom={zoom}
                recalcKey={`${nodes.length}-${collapsed.size}-${fullscreen}-${loading}`}
                canEdit={canEdit && !panMode}
                onRequestDelete={(id) => setLinkToDelete(id)}
              />
              <StickyNotesLayer
                notes={notes}
                zoom={zoom}
                canEdit={canEdit && !panMode}
                onUpdate={updateNote}
                onDelete={deleteNote}
              />
            </div>
          </div>
          {/* Search box — floats top-left over the canvas. */}
          <div className="absolute left-3 top-3 z-20 flex items-center gap-2" data-testid="org-search-box">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && matches.length) {
                    e.preventDefault();
                    const nx = (activeMatchIdx + 1) % matches.length;
                    setActiveMatchIdx(nx);
                    focusMatch(matches[nx]);
                  }
                  if (e.key === "Escape") setQuery("");
                }}
                placeholder="Şemada ara..."
                data-testid="org-search-input"
                className="h-8 w-52 rounded-lg border border-zinc-200 bg-white/95 pl-8 pr-2 text-xs shadow-md backdrop-blur placeholder:text-zinc-400 focus:outline-none focus-visible:shadow-none focus-visible:ring-1 focus-visible:ring-violet-400"
              />
            </div>
            {query.trim() && (
              <span
                data-testid="org-search-count"
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold shadow-sm ${
                  matches.length
                    ? "bg-amber-100 text-amber-800"
                    : "bg-zinc-100 text-zinc-500"
                }`}
              >
                {matches.length ? `${activeMatchIdx + 1}/${matches.length}` : "Sonuç yok"}
              </span>
            )}
          </div>
          {/* Zoom / pan toolbar — floats over the canvas. */}
          <div
            data-testid="org-zoom-toolbar"
            className="absolute right-3 top-3 z-20 flex items-center gap-0.5 rounded-xl border border-zinc-200 bg-white/95 p-1 shadow-md backdrop-blur"
          >
            <button
              type="button"
              onClick={() => setZoom((z) => clampZoom(z / 1.2))}
              data-testid="org-zoom-out"
              title="Uzaklaş"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-600 hover:bg-zinc-100"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoom(1)}
              data-testid="org-zoom-reset"
              title="%100'e dön"
              className="h-7 w-12 rounded-lg text-[11px] font-semibold tabular-nums text-zinc-700 hover:bg-zinc-100"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              onClick={() => setZoom((z) => clampZoom(z * 1.2))}
              data-testid="org-zoom-in"
              title="Yakınlaş"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-600 hover:bg-zinc-100"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={fitToScreen}
              data-testid="org-zoom-fit"
              title="Ekrana sığdır"
              className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-600 hover:bg-zinc-100"
            >
              <Maximize className="h-4 w-4" />
            </button>
            <div className="mx-0.5 h-5 w-px bg-zinc-200" />
            <button
              type="button"
              onClick={() => setPanMode((v) => !v)}
              data-testid="org-pan-toggle"
              title={panMode ? "Kaydırma modunu kapat" : "Kaydırma (pan) modu — sürükleyerek gez"}
              className={[
                "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
                panMode
                  ? "bg-violet-600 text-white shadow-sm"
                  : "text-zinc-600 hover:bg-zinc-100",
              ].join(" ")}
            >
              <Hand className="h-4 w-4" />
            </button>
            <div className="mx-0.5 h-5 w-px bg-zinc-200" />
            {canEdit && (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setLinkMode((v) => !v);
                    setLinkSource(null);
                  }}
                  data-testid="org-link-mode-toggle"
                  title={linkMode ? "Bağlantı modunu kapat" : "Kutucukları birbirine bağla"}
                  className={[
                    "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
                    linkMode
                      ? "bg-sky-600 text-white shadow-sm"
                      : "text-zinc-600 hover:bg-zinc-100",
                  ].join(" ")}
                >
                  <Link2 className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={addNote}
                  data-testid="org-add-note"
                  title="Yapışkan not ekle"
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-zinc-600 hover:bg-amber-100 hover:text-amber-700"
                >
                  <StickyNote className="h-4 w-4" />
                </button>
                <div className="mx-0.5 h-5 w-px bg-zinc-200" />
              </>
            )}
            <button
              type="button"
              onClick={toggleFullscreen}
              data-testid="org-fullscreen-toggle"
              title={fullscreen ? "Küçült (Esc)" : "Büyük görünüm"}
              className={[
                "flex h-7 w-7 items-center justify-center rounded-lg transition-colors",
                fullscreen
                  ? "bg-violet-600 text-white shadow-sm"
                  : "text-zinc-600 hover:bg-zinc-100",
              ].join(" ")}
            >
              {fullscreen ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
            </button>
          </div>
          {panMode && (
            <div
              data-testid="org-pan-hint"
              className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-zinc-900/80 px-3 py-1 text-[11px] font-medium text-white backdrop-blur"
            >
              Kaydırma modu açık — sürükleyerek gezin, tekerlekle yakınlaşın
            </div>
          )}
          {linkMode && (
            <div
              data-testid="org-link-hint"
              className="pointer-events-none absolute bottom-3 left-1/2 z-20 -translate-x-1/2 rounded-full bg-sky-600/90 px-3 py-1 text-[11px] font-medium text-white backdrop-blur"
            >
              {linkSource
                ? "Şimdi hedef kutuya tıklayın — bağlantı çizilecek"
                : "Bağlantı modu: önce kaynak kutuya tıklayın"}
            </div>
          )}
          {legendItems.length > 0 && (
            <div
              data-testid="org-legend"
              className="absolute bottom-3 left-3 z-20 max-h-44 max-w-[220px] overflow-y-auto rounded-xl border border-zinc-200 bg-white/95 p-2 shadow-md backdrop-blur"
            >
              <div className="mb-1 text-[9px] font-bold uppercase tracking-wider text-zinc-400">
                Departmanlar
              </div>
              {legendItems.map((d) => (
                <div key={d.id} className="flex items-center gap-1.5 py-0.5 text-[11px] font-medium text-zinc-700">
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-sm ${(ORG_PALETTE[d.color] || ORG_PALETTE.slate).chip}`}
                  />
                  <span className="truncate">{d.title}</span>
                </div>
              ))}
            </div>
          )}
          {multiSel.size > 0 && (
            <div
              data-testid="org-bulk-bar"
              className="absolute bottom-3 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-xl border border-zinc-200 bg-white/95 px-3 py-1.5 shadow-lg backdrop-blur"
            >
              <span className="text-[11px] font-bold text-zinc-700">
                {multiSel.size} kart seçildi
              </span>
              <div className="h-4 w-px bg-zinc-200" />
              {Object.keys(ORG_PALETTE).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => bulkColor(k)}
                  title={`Rengi ${k} yap`}
                  data-testid={`org-bulk-color-${k}`}
                  className={`h-4 w-4 rounded-full ${ORG_PALETTE[k].chip} transition-transform hover:scale-125`}
                />
              ))}
              <div className="h-4 w-px bg-zinc-200" />
              <button
                type="button"
                onClick={() => setBulkDeleteOpen(true)}
                data-testid="org-bulk-delete"
                title="Seçilenleri sil"
                className="flex h-6 w-6 items-center justify-center rounded-lg text-red-500 hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setMultiSel(new Set())}
                data-testid="org-bulk-clear"
                title="Seçimi temizle"
                className="flex h-6 w-6 items-center justify-center rounded-lg text-zinc-400 hover:bg-zinc-100"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Drag & drop action dialog — move subtree or swap people */}
      <Dialog open={!!dndDrop} onOpenChange={(o) => !o && setDndDrop(null)}>
        <DialogContent className="sm:max-w-md" data-testid="org-dnd-dialog">
          <DialogHeader>
            <DialogTitle>Sürükle & bırak — ne yapılsın?</DialogTitle>
          </DialogHeader>
          {dndDrop && (() => {
            const src = nodes.find((n) => n.id === dndDrop.srcId);
            const tgt = nodes.find((n) => n.id === dndDrop.tgtId);
            if (!src || !tgt) return null;
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 rounded-lg bg-zinc-50 px-3 py-2 text-sm">
                  <span className="font-semibold text-zinc-900">{nodeLabel(src)}</span>
                  <ArrowLeftRight className="h-3.5 w-3.5 text-zinc-400" />
                  <span className="font-semibold text-zinc-900">{nodeLabel(tgt)}</span>
                </div>
                <button
                  type="button"
                  onClick={doDndMove}
                  data-testid="org-dnd-move"
                  className="flex w-full items-start gap-3 rounded-xl border border-zinc-200 p-3 text-left transition-colors hover:border-violet-300 hover:bg-violet-50/60"
                >
                  <CornerRightDown className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                  <span>
                    <span className="block text-sm font-semibold text-zinc-900">
                      Altına taşı
                    </span>
                    <span className="block text-xs text-zinc-500">
                      "{nodeLabel(src)}" (alt dallarıyla birlikte) "{nodeLabel(tgt)}"
                      düğümünün altına bağlanır.
                    </span>
                  </span>
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => doDndBeside("left")}
                    data-testid="org-dnd-left"
                    className="flex items-start gap-2 rounded-xl border border-zinc-200 p-3 text-left transition-colors hover:border-sky-300 hover:bg-sky-50/60"
                  >
                    <MoveLeft className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                    <span>
                      <span className="block text-sm font-semibold text-zinc-900">
                        Soluna taşı
                      </span>
                      <span className="block text-xs text-zinc-500">
                        Aynı seviyede, hemen soluna yerleşir.
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => doDndBeside("right")}
                    data-testid="org-dnd-right"
                    className="flex items-start gap-2 rounded-xl border border-zinc-200 p-3 text-left transition-colors hover:border-sky-300 hover:bg-sky-50/60"
                  >
                    <MoveRight className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                    <span>
                      <span className="block text-sm font-semibold text-zinc-900">
                        Sağına taşı
                      </span>
                      <span className="block text-xs text-zinc-500">
                        Aynı seviyede, hemen sağına yerleşir.
                      </span>
                    </span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={doDndSwap}
                  data-testid="org-dnd-swap"
                  className="flex w-full items-start gap-3 rounded-xl border border-zinc-200 p-3 text-left transition-colors hover:border-emerald-300 hover:bg-emerald-50/60"
                >
                  <ArrowLeftRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <span>
                    <span className="block text-sm font-semibold text-zinc-900">
                      Kişileri değiştir
                    </span>
                    <span className="block text-xs text-zinc-500">
                      Kutular yerinde kalır; {personLabel(src)} ⇄ {personLabel(tgt)}{" "}
                      yer değiştirir.
                    </span>
                  </span>
                </button>
                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    onClick={() => setDndDrop(null)}
                    data-testid="org-dnd-cancel"
                  >
                    Vazgeç
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Link delete confirmation */}
      <Dialog open={!!linkToDelete} onOpenChange={(o) => !o && setLinkToDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="org-link-delete-dialog">
          <DialogHeader>
            <DialogTitle>Bağlantıyı sil</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            İki kutu arasındaki kesikli bağlantı çizgisi silinecek. Emin misiniz?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setLinkToDelete(null)}>Vazgeç</Button>
            <Button variant="destructive" onClick={doDeleteLink} data-testid="org-link-delete-confirm">
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk delete confirmation */}
      <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="org-bulk-delete-dialog">
          <DialogHeader>
            <DialogTitle>Seçilenleri sil</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600">
            <b>{multiSel.size}</b> kart (alt dallarıyla birlikte) silinecek. Bu işlem geri
            alınamaz. Emin misiniz?
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>Vazgeç</Button>
            <Button variant="destructive" onClick={bulkDelete} data-testid="org-bulk-delete-confirm">
              Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Editor sheet — visible to viewers too (read-only if !canEdit) */}
      <NodeEditorSheet
        node={selectedNode}
        canEdit={canEdit}
        users={users}
        onClose={() => setSelected(null)}
        onSaved={() => {
          load(activeChartId);
        }}
        onDeleted={() => {
          setSelected(null);
          load(activeChartId);
        }}
        allNodes={nodes}
      />

      {/* Create node modal */}
      <AddNodeDialog
        open={addUnder !== undefined}
        parentId={addUnder}
        chartId={activeChartId}
        users={users}
        allNodes={nodes}
        siblingHint={addSiblingHint}
        aboveId={addAboveId}
        parentNode={
          addUnder ? nodes.find((n) => n.id === addUnder) : null
        }
        onClose={() => {
          setAddUnder(undefined);
          setAddSiblingHint(null);
          setAddAboveId(null);
        }}
        onCreated={(created) => {
          setAddUnder(undefined);
          setAddSiblingHint(null);
          setAddAboveId(null);
          load(activeChartId);
          if (created?.id) setSelected(created.id);
        }}
      />

      {/* Share management modal */}
      {canEdit && (
        <ShareDialog
          open={shareDlgOpen}
          chartId={activeChartId}
          onClose={() => setShareDlgOpen(false)}
        />
      )}

      {/* Chart create dialog (new tab) */}
      <NewChartDialog
        open={newChartOpen}
        onClose={() => setNewChartOpen(false)}
        onCreate={async (name) => {
          setNewChartOpen(false);
          await addChart(name);
        }}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Chart Tabs — multi-chart selector
// -----------------------------------------------------------------------------
function ChartTabs({ charts, activeId, onSelect, canEdit, onAdd, onRename, onDelete }) {
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [menuId, setMenuId] = useState(null);

  const startRename = (c) => {
    setRenamingId(c.id);
    setRenameValue(c.name);
    setMenuId(null);
  };
  const commitRename = async () => {
    if (renamingId && renameValue.trim()) {
      await onRename(renamingId, renameValue.trim());
    }
    setRenamingId(null);
  };
  const doDelete = async (c) => {
    if (!window.confirm(`"${c.name}" şeması ve içindeki tüm düğümler silinecek. Emin misin?`))
      return;
    setMenuId(null);
    await onDelete(c.id);
  };

  return (
    <div
      data-testid="org-chart-tabs"
      className="flex flex-wrap items-center gap-1 border-b border-zinc-200 pb-1"
    >
      {charts.map((c) => {
        const active = c.id === activeId;
        return (
          <div key={c.id} className="relative">
            {renamingId === c.id ? (
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setRenamingId(null);
                }}
                data-testid={`org-chart-tab-rename-${c.id}`}
                className="rounded-t-lg border border-b-0 border-zinc-300 bg-white px-2.5 py-1.5 text-sm outline-none focus:border-violet-500"
              />
            ) : (
              <button
                type="button"
                onClick={() => onSelect(c.id)}
                onDoubleClick={() => canEdit && startRename(c)}
                data-testid={`org-chart-tab-${c.id}`}
                className={[
                  "flex items-center gap-1.5 rounded-t-lg border-b-2 px-3 py-1.5 text-[13px] font-medium transition-colors",
                  active
                    ? "border-violet-600 bg-white text-zinc-900"
                    : "border-transparent text-zinc-500 hover:text-zinc-800",
                ].join(" ")}
              >
                <span className="max-w-[180px] truncate">{c.name}</span>
                {canEdit && active && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuId(menuId === c.id ? null : c.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        setMenuId(menuId === c.id ? null : c.id);
                      }
                    }}
                    data-testid={`org-chart-tab-menu-${c.id}`}
                    aria-label="Sekme menüsü"
                    className="cursor-pointer rounded p-0.5 hover:bg-zinc-100"
                  >
                    <MoreVertical className="h-3.5 w-3.5 text-zinc-400" />
                  </span>
                )}
              </button>
            )}
            {menuId === c.id && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuId(null)}
                />
                <div
                  className="absolute left-0 top-full z-50 mt-1 w-40 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg"
                  data-testid={`org-chart-tab-menu-panel-${c.id}`}
                >
                  <button
                    type="button"
                    onClick={() => startRename(c)}
                    data-testid={`org-chart-tab-rename-btn-${c.id}`}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                  >
                    <PencilLine className="h-3.5 w-3.5" />
                    Yeniden adlandır
                  </button>
                  {charts.length > 1 && (
                    <button
                      type="button"
                      onClick={() => doDelete(c)}
                      data-testid={`org-chart-tab-delete-btn-${c.id}`}
                      className="flex w-full items-center gap-2 border-t border-zinc-100 px-3 py-2 text-left text-sm text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Sil
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        );
      })}
      {canEdit && (
        <button
          type="button"
          onClick={onAdd}
          data-testid="org-chart-tab-add"
          title="Yeni şema"
          className="ml-1 flex h-7 w-7 items-center justify-center rounded-full text-zinc-400 hover:bg-zinc-100 hover:text-violet-700"
        >
          <Plus className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// New-chart modal (lightweight)
function NewChartDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  useEffect(() => {
    if (open) setName("");
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="org-chart-new-chart-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            Yeni şema
          </DialogTitle>
        </DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">
            Şema adı
          </Label>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Örn: COP31 Etkinlik"
            className="mt-1"
            maxLength={80}
            data-testid="org-chart-new-chart-name"
            onKeyDown={(e) => {
              if (e.key === "Enter" && name.trim()) onCreate(name.trim());
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            onClick={() => name.trim() && onCreate(name.trim())}
            disabled={!name.trim()}
            data-testid="org-chart-new-chart-create"
            className="bg-zinc-900 text-white hover:bg-zinc-800"
          >
            Oluştur
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// -----------------------------------------------------------------------------
// Header
// -----------------------------------------------------------------------------
function Header({ canEdit, onAddRoot, onShare, onExport, exporting, hasData }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          Serpix
        </div>
        <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          <Network className="h-7 w-7 text-violet-600" strokeWidth={1.75} />
          Organizasyon Şeması
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-zinc-500">
          Şirket hiyerarşinizi görselleştirin. Ekipler değiştiğinde yönetim
          şemasını anında güncelleyin ve dilerseniz herkese açık bir link
          üzerinden paylaşın.
        </p>
        {canEdit && (
          <div
            data-testid="org-chart-keyboard-hint"
            className="mt-2 inline-flex flex-wrap items-center gap-1.5 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 text-[10.5px] text-zinc-600"
          >
            <span className="font-semibold uppercase tracking-wider text-zinc-500">
              Klavye
            </span>
            <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-800 shadow-sm">
              Alt
            </kbd>
            <span>+</span>
            <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-800 shadow-sm">
              ←
            </kbd>
            <span className="text-zinc-500">/</span>
            <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-800 shadow-sm">
              →
            </kbd>
            <span className="text-zinc-500">yan düğüm,</span>
            <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-800 shadow-sm">
              Enter
            </kbd>
            <span className="text-zinc-500">alt düğüm,</span>
            <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-semibold text-zinc-800 shadow-sm">
              Esc
            </kbd>
            <span className="text-zinc-500">kapat</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {hasData && (
          <div className="relative">
            <Button
              variant="outline"
              onClick={() => setMenuOpen((v) => !v)}
              disabled={exporting}
              data-testid="org-chart-export-btn"
              className="gap-2 border-zinc-300"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              Dışa Aktar
              <ChevronDown className="h-3 w-3 opacity-60" />
            </Button>
            {menuOpen && !exporting && (
              <>
                {/* click-outside catcher */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setMenuOpen(false)}
                />
                <div
                  className="absolute right-0 top-full z-50 mt-1 w-44 overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-lg"
                  data-testid="org-chart-export-menu"
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onExport("pdf");
                    }}
                    data-testid="org-chart-export-pdf"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                  >
                    <FileDown className="h-3.5 w-3.5 text-rose-600" />
                    <span>PDF olarak indir</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      onExport("png");
                    }}
                    data-testid="org-chart-export-png"
                    className="flex w-full items-center gap-2 border-t border-zinc-100 px-3 py-2 text-left text-sm text-zinc-700 hover:bg-zinc-50"
                  >
                    <ImageIcon className="h-3.5 w-3.5 text-sky-600" />
                    <span>PNG (görsel)</span>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        {canEdit && (
          <>
            <Button
              variant="outline"
              onClick={onShare}
              data-testid="org-chart-share-btn"
              className="gap-2 border-zinc-300"
            >
              <Share2 className="h-4 w-4" />
              Paylaş
            </Button>
            <Button
              onClick={onAddRoot}
              data-testid="org-chart-add-root-btn"
              className="gap-2 bg-zinc-900 text-white hover:bg-zinc-800"
            >
              <Plus className="h-4 w-4" />
              Kök düğüm
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function EmptyState({ canEdit, onAddRoot }) {
  return (
    <div
      data-testid="org-chart-empty"
      className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-zinc-300 bg-white/50 py-16 text-center"
    >
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-violet-100 text-violet-700">
        <Network className="h-7 w-7" />
      </div>
      <div className="max-w-md space-y-1">
        <div className="text-lg font-semibold text-zinc-900">
          Henüz bir organizasyon şeması yok
        </div>
        <div className="text-sm text-zinc-500">
          İlk kök düğümü ekleyerek başlayın — üst yönetim, sonrasında altına
          departman ve pozisyonları yerleştirebilirsiniz.
        </div>
      </div>
      {canEdit && (
        <Button
          onClick={onAddRoot}
          data-testid="org-chart-empty-add-btn"
          className="mt-2 gap-2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
        >
          <Plus className="h-4 w-4" />
          İlk düğümü ekle
        </Button>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Create modal (add root or child)
// -----------------------------------------------------------------------------
function AddNodeDialog({
  open,
  parentId,
  chartId,
  parentNode,
  users,
  allNodes,
  siblingHint,
  aboveId,
  onClose,
  onCreated,
}) {
  const [title, setTitle] = useState("");
  const [personName, setPersonName] = useState("");
  const [userId, setUserId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [tasks, setTasks] = useState("");
  const [color, setColor] = useState("violet");
  const [kind, setKind] = useState("person");
  const [size, setSize] = useState("md");
  const [saving, setSaving] = useState(false);
  const emailInvalid = !!email.trim() && !EMAIL_RE.test(email.trim());

  // Inherit the parent's layout so nested branches keep the shape the
  // operator originally set (no more per-add layout toggle in the UI).
  const inheritedLayout = parentNode?.layout || "horizontal";

  useEffect(() => {
    if (open) {
      setTitle("");
      setPersonName("");
      setUserId("");
      setPhone("");
      setEmail("");
      setTasks("");
      setColor("violet");
      setKind("person");
      setSize("md");
    }
  }, [open, parentId, parentNode]);

  // Lineage-aware picker sets. Same user_id can't appear on the same
  // top→bottom path; badge everyone else who's used anywhere.
  const blockedIds = useMemo(
    () => ancestorUserIds(allNodes || [], parentId),
    [allNodes, parentId],
  );
  const usedIds = useMemo(
    () => usedUserIds(allNodes || []),
    [allNodes],
  );

  // Auto-populate person_name AND phone from the selected user — user
  // can still override either. Matches the "seçilirsede ismi
  // yazdırılmalı" + "telefon otomatik gelsin" spec.
  const pickUser = (uid) => {
    setUserId(uid);
    if (!uid) return;
    const u = users.find((x) => x.id === uid);
    if (!u) return;
    if (!personName.trim()) setPersonName(u.name || u.email || "");
    if (!phone.trim() && u.phone) setPhone(u.phone);
    if (u.email) setEmail(u.email);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const isPerson = kind === "person";
      const body = {
        parent_id: parentId || null,
        chart_id: chartId || null,
        title: title.trim() || null,
        person_name: isPerson ? personName.trim() || null : null,
        user_id: isPerson ? userId || null : null,
        phone: isPerson ? phone.trim() || null : null,
        email: isPerson ? email.trim() || null : null,
        tasks: isPerson ? tasks.trim() || null : null,
        color,
        kind,
        size,
        layout: inheritedLayout,
      };
      if (siblingHint?.referenceId) {
        if (siblingHint.place === "before") body.before_id = siblingHint.referenceId;
        else body.after_id = siblingHint.referenceId;
      }
      if (aboveId) body.above_id = aboveId;
      const { data } = await api.post("/org-chart/nodes", body);
      toast.success("Düğüm eklendi");
      onCreated(data);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (status === 404) {
        toast.error(
          "Bağlı olduğun kart artık mevcut değil. Şema yenilendi — lütfen yeniden dene.",
        );
        onCreated({ id: null });
      } else {
        toast.error(detail);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="org-chart-add-dialog"
        className="max-h-[85vh] max-w-md overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" />
            {aboveId
              ? "Üstüne kök düğüm ekle"
              : parentId
                ? "Alt düğüm ekle"
                : "Yeni kök düğüm"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <KindSizePicker
            kind={kind}
            setKind={setKind}
            size={size}
            setSize={setSize}
            testPrefix="org-chart-add"
            disabled={saving}
          />
          {kind === "person" && (
          <>
          {/* 1) Kullanıcı seç */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Kullanıcıdan seç{" "}
              <span className="text-zinc-400 normal-case">(opsiyonel)</span>
            </Label>
            <div className="mt-1">
              <UserPicker
                users={users}
                value={userId}
                onChange={pickUser}
                blockedIds={blockedIds}
                usedIds={usedIds}
                testIdPrefix="org-chart-add-user"
              />
            </div>
            {blockedIds.size > 0 && (
              <p className="mt-1 flex items-start gap-1 text-[10px] text-rose-600">
                <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />
                Bu zincirde zaten seçili kullanıcılar seçilemez.
              </p>
            )}
          </div>

          {/* 2) Kişi */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Kişi{" "}
              <span className="text-zinc-400 normal-case">
                (kullanıcı seçtiyseniz otomatik dolar)
              </span>
            </Label>
            <Input
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              placeholder="Örn: Ayşe Yılmaz"
              className="mt-1"
              maxLength={200}
              data-testid="org-chart-add-person"
            />
          </div>

          {/* 3) Telefon */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Telefon{" "}
              <span className="text-zinc-400 normal-case">
                (kullanıcı seçtiyseniz otomatik dolar)
              </span>
            </Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+90 5xx xxx xx xx"
              className="mt-1"
              maxLength={64}
              data-testid="org-chart-add-phone"
            />
          </div>

          {/* 3.5) E-posta */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              E-posta{" "}
              <span className="text-zinc-400 normal-case">
                (kullanıcı seçtiyseniz otomatik dolar)
              </span>
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@sirket.com"
              className={`mt-1 ${emailInvalid ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              maxLength={200}
              data-testid="org-chart-add-email"
            />
            {emailInvalid && (
              <p
                className="mt-1 text-[11px] font-medium text-red-600"
                data-testid="org-chart-add-email-error"
              >
                Geçerli bir e-posta adresi girin (örn: ad@alan.com)
              </p>
            )}
          </div>
          </>
          )}

          {/* 4) Pozisyon / Başlık */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Pozisyon / Başlık{" "}
              <span className="text-zinc-400 normal-case">(opsiyonel)</span>
            </Label>
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Örn: Operasyon Direktörü"
              className="mt-1"
              maxLength={200}
              data-testid="org-chart-add-title"
            />
          </div>

          {/* 5) Görev / Sorumluluk */}
          {kind === "person" && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Görev / Sorumluluk{" "}
              <span className="text-zinc-400 normal-case">(opsiyonel)</span>
            </Label>
            <Textarea
              value={tasks}
              onChange={(e) => setTasks(e.target.value)}
              placeholder="Örn: Zone koordinasyonu&#10;Tedarikçi iletişimi&#10;Rapor hazırlığı"
              className="mt-1"
              rows={3}
              maxLength={4000}
              data-testid="org-chart-add-tasks"
            />
          </div>
          )}

          {/* 6) Renk */}
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wider text-zinc-500">
              Renk
            </Label>
            <ColorPicker value={color} onChange={setColor} />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
            data-testid="org-chart-add-cancel"
          >
            Vazgeç
          </Button>
          <Button
            onClick={submit}
            disabled={saving || (kind === "person" && emailInvalid)}
            data-testid="org-chart-add-submit"
            className="bg-zinc-900 text-white hover:bg-zinc-800"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Plus className="mr-2 h-4 w-4" />
            )}
            Ekle
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Editor sheet — right drawer for the selected node
// -----------------------------------------------------------------------------
function NodeEditorSheet({
  node,
  canEdit,
  users,
  onClose,
  onSaved,
  onDeleted,
  allNodes,
}) {
  const [title, setTitle] = useState("");
  const [personName, setPersonName] = useState("");
  const [userId, setUserId] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [tasks, setTasks] = useState("");
  const [color, setColor] = useState("slate");
  const [kind, setKind] = useState("person");
  const [size, setSize] = useState("md");
  const [parentId, setParentId] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [descendantCount, setDescendantCount] = useState(0);
  const emailInvalid = !!email.trim() && !EMAIL_RE.test(email.trim());

  useEffect(() => {
    if (node) {
      setTitle(node.title || "");
      setPersonName(node.person_name || "");
      setUserId(node.user_id || "");
      setPhone(node.phone || "");
      setEmail(node.email || "");
      setTasks(node.tasks || "");
      setColor(node.color || "slate");
      setKind(node.kind || "person");
      setSize(node.size || "md");
      setParentId(node.parent_id || "");
      setConfirmDelete(false);
      // Count descendants so the confirmation UX warns about cascade.
      const descendants = new Set();
      const stack = [node.id];
      while (stack.length) {
        const cur = stack.pop();
        allNodes.forEach((n) => {
          if (n.parent_id === cur && !descendants.has(n.id)) {
            descendants.add(n.id);
            stack.push(n.id);
          }
        });
      }
      setDescendantCount(descendants.size);
    }
  }, [node, allNodes]);

  if (!node) return null;

  // Reparent options: all nodes EXCEPT self and any descendants (cycle guard).
  const forbidden = new Set([node.id]);
  const stack = [node.id];
  while (stack.length) {
    const cur = stack.pop();
    allNodes.forEach((n) => {
      if (n.parent_id === cur && !forbidden.has(n.id)) {
        forbidden.add(n.id);
        stack.push(n.id);
      }
    });
  }
  const parentOptions = allNodes.filter((n) => !forbidden.has(n.id));

  // Lineage-aware picker sets: block user_ids that appear anywhere on
  // this node's ancestor chain OR descendant subtree — same person
  // can't be BOTH above and below on the same path.
  const editorBlockedIds = (() => {
    const set = ancestorUserIds(allNodes, node.parent_id);
    descendantUserIds(allNodes, node.id).forEach((id) => set.add(id));
    return set;
  })();
  const editorUsedIds = usedUserIds(allNodes);

  const pickUser = (uid) => {
    setUserId(uid);
    if (!uid) return;
    const u = (users || []).find((x) => x.id === uid);
    if (!u) return;
    if (!personName.trim()) setPersonName(u.name || u.email || "");
    if (!phone.trim() && u.phone) setPhone(u.phone);
    if (u.email) setEmail(u.email);
  };

  const save = async () => {
    setSaving(true);
    try {
      const isPerson = kind === "person";
      const payload = {
        title: title.trim() || null,
        person_name: isPerson ? personName.trim() || null : null,
        user_id: isPerson ? userId || null : null,
        phone: isPerson ? phone.trim() || null : null,
        email: isPerson ? email.trim() || null : null,
        tasks: isPerson ? tasks.trim() || null : null,
        color,
        kind,
        size,
      };
      if ((parentId || null) !== (node.parent_id || null)) {
        payload.parent_id = parentId || null;
      }
      await api.patch(`/org-chart/nodes/${node.id}`, payload);
      toast.success("Kaydedildi");
      onSaved();
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        toast.error("Bu düğüm artık mevcut değil. Şema yenilendi.");
        onDeleted();
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setSaving(true);
    try {
      const { data } = await api.delete(`/org-chart/nodes/${node.id}`);
      toast.success(
        data.deleted > 1
          ? `${data.deleted} düğüm silindi`
          : "Düğüm silindi",
      );
      onDeleted();
    } catch (err) {
      const status = err.response?.status;
      if (status === 404) {
        // Node was deleted concurrently (double-click, another tab,
        // testing agent tenant wipe). Treat as success and let the
        // parent refresh so the stale card disappears from the UI.
        toast.info("Düğüm zaten silinmiş — şema yenileniyor.");
        onDeleted();
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setSaving(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Sheet open={!!node} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="w-full max-w-md overflow-y-auto sm:max-w-md"
        data-testid="org-chart-editor"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <PencilLine className="h-4 w-4 text-violet-600" />
            {canEdit ? "Düğümü düzenle" : "Düğüm bilgisi"}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          <KindSizePicker
            kind={kind}
            setKind={setKind}
            size={size}
            setSize={setSize}
            testPrefix="org-chart-edit"
            disabled={!canEdit || saving}
          />
          {kind === "person" && (
          <>
          {/* 1) Kullanıcı seç */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Kullanıcıdan seç{" "}
              <span className="text-zinc-400 normal-case">(opsiyonel)</span>
            </Label>
            <div className="mt-1">
              <UserPicker
                users={users || []}
                value={userId}
                onChange={pickUser}
                disabled={!canEdit || saving}
                blockedIds={editorBlockedIds}
                usedIds={editorUsedIds}
                testIdPrefix="org-chart-edit-user"
              />
            </div>
          </div>

          {/* 2) Kişi */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Kişi{" "}
              <span className="text-zinc-400 normal-case">
                (kullanıcı seçtiyseniz otomatik dolar)
              </span>
            </Label>
            <Input
              value={personName}
              onChange={(e) => setPersonName(e.target.value)}
              className="mt-1"
              maxLength={200}
              disabled={!canEdit || saving}
              data-testid="org-chart-edit-person"
            />
          </div>

          {/* 3) Telefon */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Telefon{" "}
              <span className="text-zinc-400 normal-case">
                (kullanıcı seçtiyseniz otomatik dolar)
              </span>
            </Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+90 5xx xxx xx xx"
              className="mt-1"
              maxLength={64}
              disabled={!canEdit || saving}
              data-testid="org-chart-edit-phone"
            />
          </div>

          {/* 3.5) E-posta */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              E-posta{" "}
              <span className="text-zinc-400 normal-case">
                (kullanıcı seçtiyseniz otomatik dolar)
              </span>
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@sirket.com"
              className={`mt-1 ${emailInvalid ? "border-red-400 focus-visible:ring-red-400" : ""}`}
              maxLength={200}
              disabled={!canEdit || saving}
              data-testid="org-chart-edit-email"
            />
            {emailInvalid && (
              <p
                className="mt-1 text-[11px] font-medium text-red-600"
                data-testid="org-chart-edit-email-error"
              >
                Geçerli bir e-posta adresi girin (örn: ad@alan.com)
              </p>
            )}
          </div>
          </>
          )}

          {/* 4) Pozisyon / Başlık */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Pozisyon / Başlık{" "}
              <span className="text-zinc-400 normal-case">(opsiyonel)</span>
            </Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1"
              maxLength={200}
              disabled={!canEdit || saving}
              data-testid="org-chart-edit-title"
            />
          </div>

          {/* 5) Görev / Sorumluluk */}
          {kind === "person" && (
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Görev / Sorumluluk
            </Label>
            <Textarea
              value={tasks}
              onChange={(e) => setTasks(e.target.value)}
              placeholder="Bu kişiye/pozisyona atanmış görevler"
              className="mt-1"
              rows={3}
              maxLength={4000}
              disabled={!canEdit || saving}
              data-testid="org-chart-edit-tasks"
            />
          </div>
          )}

          {/* 6) Renk */}
          <div>
            <Label className="mb-1 block text-xs uppercase tracking-wider text-zinc-500">
              Renk
            </Label>
            <ColorPicker
              value={color}
              onChange={canEdit ? setColor : () => {}}
              disabled={!canEdit || saving}
            />
          </div>

          {/* 7) Üst düğüm (reparent) — hidden for view-only mode */}
          {canEdit && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">
                Üst düğüm
              </Label>
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                disabled={saving}
                data-testid="org-chart-edit-parent"
                className="mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-2 text-sm outline-none focus:border-violet-500"
              >
                <option value="">— Kök —</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title || p.person_name || "Adsız"}
                    {p.title && p.person_name ? ` (${p.person_name})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {canEdit && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 pt-4">
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              disabled={saving}
              data-testid="org-chart-edit-delete"
              className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Sil
            </Button>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
                data-testid="org-chart-edit-cancel"
              >
                Vazgeç
              </Button>
              <Button
                onClick={save}
                disabled={saving || (kind === "person" && emailInvalid)}
                data-testid="org-chart-edit-save"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                {saving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Kaydet
              </Button>
            </div>
          </div>
        )}

        {confirmDelete && (
          <Dialog
            open={confirmDelete}
            onOpenChange={(o) => !o && setConfirmDelete(false)}
          >
            <DialogContent data-testid="org-chart-delete-confirm">
              <DialogHeader>
                <DialogTitle>Düğümü sil</DialogTitle>
              </DialogHeader>
              <div className="space-y-2 text-sm text-zinc-600">
                <p>
                  <b>{node.title || node.person_name || "Adsız düğüm"}</b> silinecek
                  {descendantCount > 0 && (
                    <>
                      {" "}
                      — bu düğüme bağlı <b>{descendantCount}</b> alt düğüm de
                      birlikte silinecek.
                    </>
                  )}
                  .
                </p>
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Bu işlem geri alınamaz.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmDelete(false)}
                  disabled={saving}
                >
                  Vazgeç
                </Button>
                <Button
                  onClick={doDelete}
                  disabled={saving}
                  data-testid="org-chart-delete-confirm-btn"
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  {saving ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Sil
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </SheetContent>
    </Sheet>
  );
}

// -----------------------------------------------------------------------------
// Layout picker (yatay / dikey toggle)
// -----------------------------------------------------------------------------
function LayoutPicker({ value, onChange, disabled }) {
  const options = [
    { key: "horizontal", label: "Yatay" },
    { key: "vertical", label: "Dikey" },
  ];
  return (
    <div className="inline-flex overflow-hidden rounded-lg border border-zinc-200 bg-white text-xs">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => !disabled && onChange(o.key)}
          disabled={disabled}
          data-testid={`org-chart-layout-${o.key}`}
          className={[
            "px-3 py-1.5 font-medium transition-colors",
            value === o.key
              ? "bg-zinc-900 text-white"
              : "text-zinc-600 hover:bg-zinc-100",
            disabled ? "opacity-60" : "",
          ].join(" ")}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Color picker (chip row)
// -----------------------------------------------------------------------------
function ColorPicker({ value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {Object.keys(ORG_PALETTE).map((k) => {
        const p = ORG_PALETTE[k];
        const active = value === k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => !disabled && onChange(k)}
            disabled={disabled}
            data-testid={`org-chart-color-${k}`}
            aria-label={k}
            className={[
              "h-7 w-7 rounded-full border-2 transition-all",
              p.chip,
              active
                ? "scale-110 border-zinc-900 shadow-sm"
                : "border-transparent hover:scale-105",
              disabled ? "opacity-60" : "",
            ].join(" ")}
          />
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Share dialog — create + list + revoke public links
// -----------------------------------------------------------------------------
function ShareDialog({ open, chartId, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [days, setDays] = useState(7);
  const [copiedToken, setCopiedToken] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org-chart/shares", {
        params: chartId ? { chart_id: chartId } : {},
      });
      setShares(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [chartId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/org-chart/share", {
        days: Number(days) || 7,
        chart_id: chartId || null,
      });
      // Yeni link otomatik panoya kopyalanır — ayrıca kopyala butonuna gerek yok.
      let copied = false;
      if (data?.token) {
        try {
          await navigator.clipboard.writeText(publicUrl(data.token));
          copied = true;
          setCopiedToken(data.token);
          setTimeout(() => setCopiedToken(null), 3000);
        } catch {
          /* clipboard izni yoksa sadece oluşturuldu mesajı kalır */
        }
      }
      toast.success(
        copied ? "Bağlantı oluşturuldu ve panoya kopyalandı" : "Bağlantı oluşturuldu",
      );
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Bu paylaşım linki iptal edilecek. Devam edilsin mi?"))
      return;
    try {
      await api.delete(`/org-chart/share/${token}`);
      toast.success("Bağlantı iptal edildi");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const publicUrl = (token) =>
    `${window.location.origin}/org-chart-share/${token}`;

  const copy = async (token) => {
    try {
      await navigator.clipboard.writeText(publicUrl(token));
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1500);
    } catch {
      /* no-op */
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="org-chart-share-dialog"
        className="max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-violet-600" />
            Organizasyon şemasını paylaş
          </DialogTitle>
        </DialogHeader>

        <div className="rounded-lg border border-zinc-200 bg-zinc-50/50 p-3">
          <div className="mb-2 text-xs text-zinc-600">
            Herkese açık, yalnızca okuma linki oluşturun. Link, aşağıdaki gün
            sayısı kadar geçerli olur ve dilediğiniz an iptal edebilirsiniz.
          </div>
          <div className="flex items-end gap-2">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-zinc-500">
                Geçerlilik (gün)
              </Label>
              <Input
                type="number"
                min={1}
                max={90}
                value={days}
                onChange={(e) => setDays(e.target.value)}
                className="mt-1 w-24"
                data-testid="org-chart-share-days"
              />
            </div>
            <Button
              onClick={create}
              disabled={creating}
              data-testid="org-chart-share-create"
              className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:from-violet-700 hover:to-indigo-700"
            >
              {creating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <LinkIcon className="mr-2 h-4 w-4" />
              )}
              Yeni bağlantı
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <div className="mb-1 text-[11px] uppercase tracking-wider text-zinc-500">
            Aktif bağlantılar
          </div>
          {loading ? (
            <div className="flex h-16 items-center justify-center">
              <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
            </div>
          ) : shares.length === 0 ? (
            <div className="rounded-md border border-dashed border-zinc-200 p-4 text-center text-xs text-zinc-400">
              Henüz aktif bağlantı yok.
            </div>
          ) : (
            <ul
              className="space-y-1.5 max-h-56 overflow-y-auto pr-1"
              data-testid="org-chart-share-list"
            >
              {shares.map((s) => {
                const expiresLabel = new Date(s.expires_at).toLocaleString(
                  "tr-TR",
                  { dateStyle: "medium", timeStyle: "short" },
                );
                return (
                  <li
                    key={s.token}
                    className="flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-2.5 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-[11px] text-zinc-800">
                        {publicUrl(s.token)}
                      </div>
                      <div className="mt-0.5 text-[10px] text-zinc-400">
                        Bitiş: {expiresLabel}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => copy(s.token)}
                      data-testid={`org-chart-share-copy-${s.token}`}
                      className="rounded-md p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
                      title="Kopyala"
                    >
                      {copiedToken === s.token ? (
                        <Check className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => revoke(s.token)}
                      data-testid={`org-chart-share-revoke-${s.token}`}
                      className="rounded-md p-1.5 text-red-600 hover:bg-red-50"
                      title="İptal et"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// re-export helpers used by the public view.
export { buildTree };
